import { gql, useQuery } from "@apollo/client";
import { useFlags } from "@resource/client-ffs";
import withPageAuthRequired from "auth/withPageAuthRequired";
import { V2IntegrationsPage } from "client/app/(account settings)/integrations/page";
import Layout from "components/Layout";
import Loading from "components/legacy/Loading/Loading";
import {
  GreenhouseIntegration,
  Integration,
  MergeIntegration,
  SlackIntegration,
} from "components/legacy/Settings/Integrations/types";
import SettingsLayout from "components/legacy/Settings/SettingsLayout";
import SlackIntegrationCard from "components/legacy/Settings/SlackIntegration/SlackIntegrationCard";
import GreenhouseIntegrationCard from "components/Settings/GreenhouseIntegration/GreenhouseIntegrationCard";
import MergeIntegrationCard from "components/Settings/MergeIntegration/MergeIntegrationCard";
import FeatureFlags from "generated/FeatureFlags";
import {
  CustomerIntegrationsData,
  IntegrationEnum,
} from "generated/schemaTypes";
import _ from "lodash";
import React from "react";
import { PermissionEnum } from "utils/permissions";

const INTEGRATIONS_DATA = gql`
  query CustomerIntegrationsData {
    currentUserPrisma {
      id
      currentUserMembership {
        id
        hasLimitedAccess
      }
      currentOrganization {
        id
        customer {
          id
          greenhouseIntegration {
            id
            type
            connected
            apiKeySuffix
            maildropAddress
            subdomain
            requiredWebhooksConfigured
            onBehalfOfUserId
          }
          slackIntegration {
            id
            type
            connected
            auth0SlackUserId
            autopopulateInterviewer
            needsReauthentication
            notificationsChannelId
            receiveNotifications
          }
          leverIntegration {
            id
            mergeAccountId
            type
            connected
          }
          greenhouseMergeIntegration {
            id
            mergeAccountId
            type
            connected
          }
        }
      }
    }
  }
`;

function LegacyIntegrations() {
  const { data, loading, refetch } =
    useQuery<CustomerIntegrationsData>(INTEGRATIONS_DATA);
  const user = data?.currentUserPrisma;

  const { [FeatureFlags.MERGE_SYNC]: mergeSyncEnabled } = useFlags();

  let integrations: (
    | GreenhouseIntegration
    | SlackIntegration
    | MergeIntegration
  )[] = [];

  const customer = data?.currentUserPrisma?.currentOrganization?.customer;
  if (customer) {
    integrations = [
      {
        ...customer.greenhouseIntegration,
        issues: [],
      },
      {
        ...customer.slackIntegration,
        issues: [],
      },
      {
        ...customer.leverIntegration,
        issues: [],
      },
      {
        ...customer.greenhouseMergeIntegration,
        issues: [],
      },
    ];
  }

  const isATSConnected = (type: IntegrationEnum) =>
    !!_.find(integrations, {
      type,
      connected: true,
    });

  const enabledIntegrations = _.filter(integrations, (integration) => {
    switch (integration.type) {
      case IntegrationEnum.SLACK:
        return (
          isATSConnected(IntegrationEnum.GREENHOUSE) ||
          isATSConnected(IntegrationEnum.GREENHOUSE_MERGE) ||
          isATSConnected(IntegrationEnum.LEVER)
        );
      case IntegrationEnum.GREENHOUSE:
        return (
          !user?.currentUserMembership?.hasLimitedAccess && !mergeSyncEnabled
        );
      case IntegrationEnum.LEVER:
      case IntegrationEnum.GREENHOUSE_MERGE:
        return (
          !user?.currentUserMembership?.hasLimitedAccess && mergeSyncEnabled
        );
      default:
        return false;
    }
  });

  const afterMergeConnect = () => {
    refetch();
  };

  const IntegrationComponents = _(enabledIntegrations)
    .orderBy(["connected", "type"], ["desc", "asc"])
    .map((integration: Integration | MergeIntegration) => {
      function Integration() {
        switch (integration.type) {
          case IntegrationEnum.SLACK:
            return React.createElement(SlackIntegrationCard, {
              ...(integration as SlackIntegration),
            });
          case IntegrationEnum.GREENHOUSE:
            return React.createElement(GreenhouseIntegrationCard, {
              ...integration,
            });
          case IntegrationEnum.LEVER:
          case IntegrationEnum.GREENHOUSE_MERGE:
            return React.createElement(MergeIntegrationCard, {
              ...(integration as MergeIntegration),
              afterMergeConnect,
            });
          default:
            return null;
        }
      }
      return Integration;
    })
    // eslint-disable-next-line @typescript-eslint/ban-types
    .value() as unknown as React.FC[];

  return loading ? (
    <Loading />
  ) : (
    <>
      <div className="flex flex-row flex-wrap gap-6">
        {_.map(IntegrationComponents, (IntegrationComponent, index) => (
          <IntegrationComponent key={index} />
        ))}
      </div>
    </>
  );
}
function IntegrationsPage() {
  const { [FeatureFlags.NEW_GUIDE_ARCHITECTURE]: newGuideArchitectureEnabled } =
    useFlags();

  if (newGuideArchitectureEnabled) {
    return <V2IntegrationsPage />;
  }

  return <LegacyIntegrations />;
}

export default withPageAuthRequired(IntegrationsPage, {
  requiredPermissions: PermissionEnum.ORGANIZATION_INTEGRATIONS_WRITE,
  getLayout: (page) => (
    <Layout>
      <SettingsLayout>{page}</SettingsLayout>
    </Layout>
  ),
});
